import { useTranslation } from "react-i18next";
import { CustomQuotationOtherTypeBadge } from "@/components/shared/badge/customQuotationOtherType.badge";
import { CustomQuotationTypeBadge } from "@/components/shared/badge/customQuotationType.badge";
import {
  PalettePackageInfo,
  PaletteProductInfo,
} from "@/components/backOffice/pendingCustomQuotation/modal/paletteInfo";
import {
  ParcelPackageInfo,
  ParcelProductInfo,
} from "@/components/backOffice/pendingCustomQuotation/modal/parcelInfo";
import { CustomQuotationTypeEnum } from "@/constants/enums/customQuotationType.enum";
import { OtherCustomQuotationTypeEnum } from "@/constants/enums/otherCustomQuotationType.enum";
import { GetOneExpeditionBoResponseDto } from "@/types/api/api-types";

const InformationDisplay = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-row gap-2">
    <p className="min-w-fit text-sm text-gray-900">{label} :</p>
    <p className="text-sm text-gray-900">{value}</p>
  </div>
);

export const SideContentProductInfo = ({
  expedition,
}: {
  expedition: GetOneExpeditionBoResponseDto;
}) => {
  const { t } = useTranslation(["boExpeditionForm", "common"]);
  if (expedition.quotation || !expedition.customQuotation) return null;

  const customQuotation = expedition.customQuotation;
  const customQuotationType = expedition.customQuotation.type as CustomQuotationTypeEnum;

  if (customQuotationType === CustomQuotationTypeEnum.OTHER) {
    const otherDetail = customQuotation.otherDetail;

    return (
      <div className="flex w-full flex-col gap-2 rounded-md bg-slate-100 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-semibold text-gray-900">
            {t("sidePanelProductInfo.additionalInfoTitle")}
          </p>
          <CustomQuotationOtherTypeBadge
            type={otherDetail?.otherCustomQuotationType as OtherCustomQuotationTypeEnum | undefined}
          />
        </div>
        <InformationDisplay
          label={t("sidePanelProductInfo.multipleExpeditionLabel")}
          value={
            otherDetail?.isMultipleExpedition ? t("common:boolean.yes") : t("common:boolean.no")
          }
        />
        <InformationDisplay
          label={t("sidePanelProductInfo.numberOfExpeditionLabel")}
          value={(otherDetail?.numberOfExpedition ?? 1).toString()}
        />
      </div>
    );
  }

  const isPalette = customQuotationType === CustomQuotationTypeEnum.PALETTE;

  if (isPalette) {
    const paletteProducts = customQuotation.paletteDetail?.paletteProducts ?? [];
    const palettePackages = customQuotation.paletteDetail?.palettePackages ?? [];

    return (
      <div className="flex w-full flex-col gap-2 rounded-md bg-slate-100 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-semibold text-gray-900">
            {t("sidePanelProductInfo.productInfoTitle")}
          </p>
          <CustomQuotationTypeBadge type={customQuotationType} />
        </div>
        <p className="text-sm text-gray-500">{t("sidePanelProductInfo.productsLabel")}</p>
        {paletteProducts.map((paletteProduct, index) => (
          <PaletteProductInfo key={index} paletteProduct={paletteProduct} index={index} />
        ))}
        {paletteProducts.length === 0 && <p className="text-sm text-gray-900">-</p>}
        <p className="text-sm text-gray-500">{t("sidePanelProductInfo.dimensionsLabel")}</p>
        {palettePackages.map((palettePackage, index) => (
          <PalettePackageInfo key={index} palettePackage={palettePackage} index={index} />
        ))}
        {palettePackages.length === 0 && <p className="text-sm text-gray-900">-</p>}
      </div>
    );
  }

  const parcelProducts = customQuotation.parcelDetail?.parcelProducts ?? [];
  const parcelPackages = customQuotation.parcelDetail?.parcelPackages ?? [];

  return (
    <div className="flex w-full flex-col gap-2 rounded-md bg-slate-100 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm font-semibold text-gray-900">
          {t("sidePanelProductInfo.productInfoTitle")}
        </p>
        <CustomQuotationTypeBadge type={customQuotationType} />
      </div>
      <p className="text-sm text-gray-500">{t("sidePanelProductInfo.productsLabel")}</p>
      {parcelProducts.map((parcelProduct, index) => (
        <ParcelProductInfo key={index} parcelProduct={parcelProduct} index={index} />
      ))}
      {parcelProducts.length === 0 && <p className="text-sm text-gray-900">-</p>}
      <p className="text-sm text-gray-500">{t("sidePanelProductInfo.dimensionsLabel")}</p>
      {parcelPackages.map((parcelPackage, index) => (
        <ParcelPackageInfo key={index} parcelPackage={parcelPackage} index={index} />
      ))}
      {parcelPackages.length === 0 && <p className="text-sm text-gray-900">-</p>}
    </div>
  );
};
